/**
 * Authentication + tenant context.
 *
 * On boot we probe the allauth session, then (if a tenant slug is known) load
 * `/api/v1/me` which carries roles, permissions and enabled modules. Feature
 * code uses `useAuth().can("workorders.create")` for UI-level gating; the
 * backend remains the source of truth.
 */
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import * as authApi from "@/api/auth";
import { api, getTenantSlug, setTenantSlug, type Schemas } from "@/api/client";
import { ApiError, unwrap } from "@/api/problem";

/** The `/me` payload. */
export type Me = Schemas["MeOut"];

/** Auth state machine. */
export type AuthStatus = "booting" | "anonymous" | "authenticated";

/** Value exposed by `useAuth`. */
export interface AuthContextValue {
  status: AuthStatus;
  me: Me | null;
  tenantSlug: string;
  /** Error from the last `/me` load (e.g. no membership in the tenant). */
  bootstrapError: ApiError | null;
  login: (tenant: string, email: string, password: string) => Promise<void>;
  verifyMfa: (tenant: string, code: string) => Promise<void>;
  switchTenant: (tenant: string) => Promise<void>;
  refreshMe: () => Promise<boolean>;
  logout: () => Promise<void>;
  /** True when the member holds the permission (platform admins always pass). */
  can: (permission: string) => boolean;
  /** True when the tenant has the module enabled. */
  hasModule: (module: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Load the bootstrap payload for the active tenant. */
export async function fetchMe(): Promise<Me> {
  return unwrap(await api.GET("/api/v1/me"));
}

/** Provide auth state to the tree. */
export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>("booting");
  const [me, setMe] = useState<Me | null>(null);
  const [tenantSlug, setSlug] = useState<string>(() => getTenantSlug());
  const [bootstrapError, setBootstrapError] = useState<ApiError | null>(null);

  const loadMe = useCallback(async (): Promise<boolean> => {
    try {
      const data = await fetchMe();
      setMe(data);
      setBootstrapError(null);
      return true;
    } catch (err) {
      setMe(null);
      setBootstrapError(err instanceof ApiError ? err : new ApiError(0, { detail: String(err) }));
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      const user = await authApi.fetchSession();
      if (cancelled) {
        return;
      }
      if (!user) {
        setStatus("anonymous");
        return;
      }
      const ok = getTenantSlug() ? await loadMe() : false;
      if (!cancelled) {
        setStatus(ok ? "authenticated" : "anonymous");
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [loadMe]);

  const login = useCallback(
    async (tenant: string, email: string, password: string): Promise<void> => {
      const slug = tenant.trim().toLowerCase();
      await authApi.login(slug, email, password);
      setTenantSlug(slug);
      setSlug(slug);
      const ok = await loadMe();
      if (!ok) {
        throw new ApiError(403, { code: "no_membership", detail: "No access to workspace" });
      }
      setStatus("authenticated");
    },
    [loadMe],
  );

  const verifyMfa = useCallback(
    async (tenant: string, code: string): Promise<void> => {
      const slug = tenant.trim().toLowerCase();
      await authApi.authenticateMfa(slug, code);
      setTenantSlug(slug);
      setSlug(slug);
      const ok = await loadMe();
      if (!ok) {
        throw new ApiError(403, { code: "no_membership", detail: "No access to workspace" });
      }
      setStatus("authenticated");
    },
    [loadMe],
  );

  const switchTenant = useCallback(
    async (tenant: string): Promise<void> => {
      const slug = tenant.trim().toLowerCase();
      setTenantSlug(slug);
      setSlug(slug);
      qc.clear();
      const ok = await loadMe();
      if (ok) {
        setStatus("authenticated");
      }
    },
    [loadMe, qc],
  );

  const logout = useCallback(async (): Promise<void> => {
    await authApi.logout();
    qc.clear();
    setMe(null);
    setStatus("anonymous");
  }, [qc]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      me,
      tenantSlug,
      bootstrapError,
      login,
      verifyMfa,
      switchTenant,
      refreshMe: loadMe,
      logout,
      can: (permission): boolean =>
        !!me && (me.is_platform_admin || me.permissions.includes(permission)),
      hasModule: (module): boolean => !!me && me.modules.includes(module),
    }),
    [status, me, tenantSlug, bootstrapError, login, verifyMfa, switchTenant, loadMe, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Access the auth context; throws outside `AuthProvider`. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
